import { useEffect, useState } from "react";

import AddRounded from "@mui/icons-material/AddRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Box, Button, IconButton, MenuItem, Select, Stack, Typography } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";

import { inputTypes, selectOptionsKeyName } from "data/globalData";
import { WhiteBgBox } from "../TrimsSection/TrimsSection";
import { fabricCategoryColumn, fabricFinalColumns } from "components/PimDashboard/DashboardComponents/Sourcing/FabricMaster/data";
import { fabricComponentColumn, fabricConsumptionColumns } from "./data";
import {
	fabricDetailsCompleteApi,
	sectionCompletedStatus,
	sectionDetailsColumns,
} from "../data";
import SectionCompleteButton from "../SectionCompleteButton";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { fabricCodeSearchColumn } from "components/PimDashboard/DashboardComponents/Sourcing/FabricStylesDashboard/EditFabricStyleDetails/data";

const colStyle = {
	headerAlign: "center",
	align: "center",
	width: 133,
	flex: 1,
};

const fabricComponentOptions = ["SHELL FABRIC", "CONTRAST FABRIC", "TRIM FABRIC", "LINING"];

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getNextFabricComponentName = (baseComponent = "", rows = []) => {
	const normalizedBaseComponent = baseComponent?.trim();
	if (!normalizedBaseComponent) return "";

	const componentPattern = new RegExp(`^${escapeRegExp(normalizedBaseComponent)}(?:\\s+(\\d+))?$`);
	const nextIndex =
		rows.reduce((maxIndex, row) => {
			const currentComponent = row?.[fabricComponentColumn.key] ?? row?.fabric_role ?? "";
			const matchedComponent = currentComponent.match(componentPattern);
			if (!matchedComponent) return maxIndex;
			const currentIndex = matchedComponent[1] ? Number(matchedComponent[1]) : 1;
			return Math.max(maxIndex, currentIndex);
		}, 0) + 1;

	return nextIndex === 1 ? normalizedBaseComponent : `${normalizedBaseComponent} ${nextIndex}`;
};

const valueOptions = {
	[fabricComponentColumn.key]: fabricComponentOptions,
	[fabricConsumptionColumns.consumption_unit.key]: ["Mtr", "Yard", "Kg"],
};
const localRowIdKey = "__local_row_id";

const normalizeFabricRow = (row, index = 0) => {
	const fabricComponent = row?.[fabricComponentColumn.key] ?? row?.fabric_role ?? "";
	return {
		...row,
		[fabricComponentColumn.key]: fabricComponent,
		[localRowIdKey]: row?.[localRowIdKey] ?? row?.id ?? `${row?.fabric_code ?? "fabric"}-${fabricComponent || "component"}-${index}`,
	};
};

const FabricSection = ({
	styleId,
	styleFabricArr = [],
	sectionDetails,
	onSectionDetailsUpdate = () => {},
	setStyleFabricArr = () => {},
	updatedFabricArr = [],
	setUpdatedFabricArr = () => {},
	enableConsumptionEdit = false,
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const [searchObj, setSearchObj] = useState({});
	const [isEditMode, setIsEditMode] = useState(false);
	const defFabricTableColumns = [
		{
			...fabricComponentColumn,
			...colStyle,
			width: 120,
			headerName: fabricComponentColumn.name,
			field: fabricComponentColumn.key,
		},
		{
			...fabricFinalColumns.fabric_code,
			...colStyle,
			width: 120,
			headerName: fabricFinalColumns.fabric_code.name,
			field: fabricFinalColumns.fabric_code.key,
		},
		{
			...fabricCategoryColumn,
			...colStyle,
			headerName: fabricCategoryColumn.name,
			field: fabricCategoryColumn.key,
			// renderCell: ({ field, row }) => {
			// 	return <WhiteBgBox field={field} row={row} />;
			// },
			editable: false,
		},
		{
			...fabricFinalColumns.fabric_quality,
			field: fabricFinalColumns.fabric_quality.key,
			headerName: fabricFinalColumns.fabric_quality.name,
			...colStyle,
		},
		{
			...fabricFinalColumns.fabric_name,
			field: fabricFinalColumns.fabric_name.key,
			headerName: fabricFinalColumns.fabric_name.name,
			...colStyle,
		},
		{
			...fabricFinalColumns.fabric_sort_no,
			field: fabricFinalColumns.fabric_sort_no.key,
			headerName: fabricFinalColumns.fabric_sort_no.name,
			...colStyle,
		},
		{
			...fabricConsumptionColumns.consumption,
			field: fabricConsumptionColumns.consumption.key,
			headerName: fabricConsumptionColumns.consumption.name,
			...colStyle,
			...(enableConsumptionEdit
				? {
						editable: true,
						type: "number",
						renderCell: ({ field, row }) => {
							return <WhiteBgBox field={field} row={row} />;
						},
					}
				: {}),
		},
		{
			...fabricConsumptionColumns.consumption_unit,
			field: fabricConsumptionColumns.consumption_unit.key,
			headerName: fabricConsumptionColumns.consumption_unit.name,
			...colStyle,
			...(enableConsumptionEdit
				? {
						editable: true,
						type: "singleSelect",
						valueOptions: valueOptions[fabricConsumptionColumns.consumption_unit.key],
						renderCell: ({ field, row }) => {
							return <WhiteBgBox field={field} row={row} />;
						},
					}
				: {}),
		},
		{
			headerName: "Delete",
			width: 120,
			...colStyle,
			renderCell: ({ row }) => {
				return (
					<IconButton onClick={() => handleDeleteFabricClick(row)}>
						<DeleteRounded />
					</IconButton>
				);
			},
		},
	];
	const [fabricArr, setFabricArr] = useState(styleFabricArr?.map(normalizeFabricRow) ?? []);

	useEffect(() => {
		setFabricArr(styleFabricArr?.map(normalizeFabricRow) ?? []);
	}, [styleFabricArr]);

	const handleCancelClick = () => {
		setFabricArr(styleFabricArr?.map(normalizeFabricRow) ?? []);
		setUpdatedFabricArr([]);
		setFabricToAdd({});
		setSearchObj({});
		setIsEditMode(false);
	};

	const [fabricToAdd, setFabricToAdd] = useState({});
	const fetchFabricMasterdataFromSearch = async (offset = 0, limit = 20, searchTerm = "") => {
		const query = {
			limit,
			offset,
		};
		if (searchTerm) query.search = searchTerm;
		return await axios.get(`/api/designer/get_fabric_data_by_search`, {
			params: query,
			query: {},
		});
	};
	const handleFabricCodeSelect = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(searchObj);
		setSearchObj(dataObj ?? {});
	};
	const handleAddFabricClick = () => {
		let obj = fabricToAdd;
		if (!obj || !obj[fabricComponentColumn.key] || obj[fabricComponentColumn.key] === inputTypes.SELECT.key) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Select Fabric Component`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		const selectedFabricCode = searchObj?.[fabricCodeSearchColumn.key];
		if (!selectedFabricCode?.[fabricFinalColumns.fabric_code.key]) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Select Fabric Code `,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		const nextFabricComponent = getNextFabricComponentName(obj[fabricComponentColumn.key], fabricArr);
		let objToAdd = {
			...selectedFabricCode,
			[fabricComponentColumn.key]: nextFabricComponent,
			[fabricConsumptionColumns.consumption_unit.key]: selectedFabricCode?.[fabricConsumptionColumns.consumption_unit.key] ?? "",
			is_new: true,
			id: new Date().valueOf(),
			[localRowIdKey]: new Date().valueOf(),
		};
		if (fabricArr) setFabricArr([...fabricArr, objToAdd]);
		else setFabricArr([objToAdd]);
		setUpdatedFabricArr([...updatedFabricArr, objToAdd]);
		setFabricToAdd({});
		setSearchObj({});
	};

	const handleDeleteFabricClick = (row) => {
		if (row?.is_new) {
			setFabricArr((prev) => prev.filter((f) => f?.[localRowIdKey] !== row?.[localRowIdKey]));
			setUpdatedFabricArr((prev) => prev.filter((f) => f?.[localRowIdKey] !== row?.[localRowIdKey]));
		} else {
			setFabricArr((prev) => prev.filter((f) => f?.[localRowIdKey] !== row?.[localRowIdKey]));
			setUpdatedFabricArr((prev) => [
				...prev.filter((f) => f?.[localRowIdKey] !== row?.[localRowIdKey]),
				{ id: row.id, is_deleted: true },
			]);
		}
	};

	const handleRowChange = (updatedRow, originalRow) => {
		const rowToAdd = { ...updatedRow, updated_by: user?.email, [localRowIdKey]: originalRow?.[localRowIdKey] };
		if (updatedFabricArr?.length)
			setUpdatedFabricArr([
				...updatedFabricArr?.filter((row) => row?.[localRowIdKey] !== originalRow?.[localRowIdKey]),
				rowToAdd,
			]);
		else setUpdatedFabricArr([rowToAdd]);

		setFabricArr(
			fabricArr?.map((row) => {
				if (row?.[localRowIdKey] === originalRow?.[localRowIdKey]) return rowToAdd;
				else return row;
			}),
		);
		return rowToAdd;
	};

	const handleSaveClick = async () => {
		if (!updatedFabricArr?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No Changes To Save",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const payload = {
				fabricArr: updatedFabricArr.map(({ [localRowIdKey]: _localRowId, ...row }) => row),
			};
			const res = await axios.post(`/api/fabric_trims/save_fabric_data?styleId=${styleId}`, payload);

			const sanitizedFabricArr =
				res?.data?.result?.createdFabricData?.length || res?.data?.result?.updateFabricData?.length
					? [
							...(styleFabricArr ?? []).filter((row) => {
								const updatedIds = payload.fabricArr.filter((item) => item?.id).map((item) => item.id);
								return !updatedIds.includes(row?.id);
							}),
							...(res?.data?.result?.createdFabricData ?? []),
							...(res?.data?.result?.updateFabricData ?? []),
						]
					: fabricArr.map(({ [localRowIdKey]: _localRowId, ...row }) => row);
			setStyleFabricArr(sanitizedFabricArr);
			setUpdatedFabricArr([]);
			setIsEditMode(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Fabric saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const isSectionCompleted = sectionDetails?.[sectionDetailsColumns.is_completed.key] === sectionCompletedStatus;

	const handleCompleteClick = async () => {
		setLoadingOpen(true);
		try {
			const res = await axios.post(`${fabricDetailsCompleteApi}?styleId=${styleId}`);
			const updatedDetails = res?.data?.result;
			onSectionDetailsUpdate(updatedDetails ?? {});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Fabric section marked complete",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Box
			sx={{
				border: `1px solid ${grey[300]}`,
				borderRadius: 2,
				overflow: "hidden",
				backgroundColor: "#fff",
			}}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="flex-start"
				rowGap={1.5}
				flexWrap="wrap"
				px={2}
				py={1.5}
				sx={{
					backgroundColor: grey[50],
					borderBottom: `1px solid ${grey[300]}`,
				}}>
				<Stack spacing={1.25} flex={1} minWidth={320}>
					<Box display="flex" alignItems="center" columnGap={1.25} flexWrap="wrap" rowGap={0.75}>
						<Typography variant="h6" sx={{ fontWeight: 700, color: grey[900] }}>
							Fabric
						</Typography>
						{isSectionCompleted && (
							<Typography variant="caption" sx={{ color: grey[600] }}>
								Completed by {sectionDetails?.[sectionDetailsColumns.completed_by.key] || "—"}
							</Typography>
						)}
					</Box>
					{isEditMode && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "center",
								columnGap: 1,
								rowGap: 1,
							}}>
							<Select
								sx={{
									width: 170,
									backgroundColor: "#fff",
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: grey[300],
									},
									borderRadius: 1,
								}}
								size="small"
								fullWidth
								variant="outlined"
								displayEmpty
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								value={
								fabricToAdd && fabricToAdd[fabricComponentColumn.key]
									? fabricToAdd[fabricComponentColumn.key]
										: inputTypes.SELECT.key
								}
								renderValue={(selected) =>
									selected === inputTypes.SELECT.key ? (
									<span style={{ color: grey[400] }}>Select Component</span>
									) : (
										selected
									)
								}
							onChange={(e) => setFabricToAdd({ ...fabricToAdd, [fabricComponentColumn.key]: e.target.value })}>
								<MenuItem value={inputTypes.SELECT.key}>
								<span style={{ color: grey[400] }}>Select Component</span>
								</MenuItem>
							{valueOptions[fabricComponentColumn.key]?.map((row) => {
									return (
										<MenuItem key={row} value={row}>
											{row}
										</MenuItem>
									);
								})}
							</Select>
							<SearchInputFieldWithDebounce
								column={fabricCodeSearchColumn}
								data={searchObj}
								setData={handleFabricCodeSelect}
								fetchData={fetchFabricMasterdataFromSearch}
								selectOptKeyName={selectOptionsKeyName}
								disableLabel
								storeValueAs="object"
								width="240px"
								inputSx={{
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#fff",
									},
								}}
							/>

							<Button
								size="small"
								onClick={handleAddFabricClick}
								variant="contained"
								startIcon={<AddRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Add Fabric
							</Button>
						</Box>
					)}
				</Stack>
				<Box display="flex" columnGap={1} alignItems="center" flexWrap="wrap" rowGap={1}>
					{isEditMode ? (
						<>
							<Button
								size="small"
								variant="outlined"
								onClick={handleCancelClick}
								startIcon={<CloseRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Cancel
							</Button>
							<Button
								size="small"
								variant="contained"
								onClick={handleSaveClick}
								startIcon={<SaveRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Save
							</Button>
						</>
					) : (
						<>
							<SectionCompleteButton sectionDetails={sectionDetails} onComplete={handleCompleteClick} />
							<Button
								size="small"
								variant="contained"
								onClick={() => setIsEditMode(true)}
								startIcon={<EditRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Edit
							</Button>
						</>
					)}
				</Box>
			</Box>
			<div width="100%">
				<DataGrid
					columns={defFabricTableColumns}
					rows={fabricArr ? fabricArr : []}
					getRowId={(row) => row?.[localRowIdKey]}
					slotProps={{
						baseSelect: {
							MenuProps: {
								PaperProps: {
									sx: {
										maxHeight: 250,
										width: 200,
										// overflow: "scroll",
									},
								},
							},
						},
					}}
					sx={{
						height: 170,
						background: "#fff",
						border: "none",
						"& .MuiDataGrid-row": {
							cursor: "pointer",
							backgroundColor: "#fff",
							"&:nth-of-type(even)": {
								backgroundColor: grey[50],
							},
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							backgroundColor: grey[100],
							borderBottom: `1px solid ${grey[300]}`,
						},
						"& .MuiDataGrid-cell": {
							padding: 0,
							borderBottom: `1px solid ${grey[200]}`,
						},
						"& .MuiDataGrid-cell:focus-within": {
							outline: `1px solid ${blue[400]}`,
							outlineOffset: "-1px",
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[100],
							borderRadius: 0,
						},
					}}
					columnHeaderHeight={35}
					disableRowSelectionOnClick
					rowHeight={35}
					showCellVerticalBorder
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					processRowUpdate={handleRowChange}
					hideFooter
				/>
			</div>
		</Box>
	);
};

export default FabricSection;
